import { Component, inject, OnDestroy } from '@angular/core'
import { ModalHeaderComponent } from '../modal-header/modal-header.component'
import { ModalBodyComponent } from '../modal-body/modal-body.component'
import { ModalFooterComponent } from '../modal-footer/modal-footer.component'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { ActiveFilterListComponent } from '../../table/filters/active-filter-list/active-filter-list.component'
import { ServicePointIdReference } from 'src/app/shared/models/referenceData'
import { ServicePointSelectorComponent } from '../../service-point-selector/service-point-selector.component'
import { LabeledPropertyComponent } from '../../labeled-property/labeled-property.component'
import {
  ServicePointGroupSelectorComponent,
} from '../../service-point-group-selector/service-point-group-selector.component'
import { ServicePointFilter } from 'src/app/shared/services/filter/service-point-filter'
import { ServicePointGroupFilter } from 'src/app/shared/services/filter/service-point-group-filter'
import { Subscription } from 'rxjs'
import { Filter } from 'src/app/shared/services/filter/filter.service'
import { ServicePointSearchFilter } from 'src/app/shared/services/filter/service-point-search-filter'
import { HeaderModal } from '../../table/headers/table-header.component'
import { ServicePointGroupWithRefs } from '../../service-point-groups/service-point-group.types'

interface ServicePointFilterModalInit {
  groupField?: string;
  partialField?: string;
}

@Component({
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    LabeledPropertyComponent,
    ServicePointSelectorComponent,
    ServicePointGroupSelectorComponent,
    ActiveFilterListComponent,
  ],
  selector: 'app-service-point-filter-modal',
  styles: `
  `,
  template: `
  @if(filter){
    <div app-modal-header>{{ filter.state.config.meta.fieldDisplay }}</div>
    <div app-modal-body>
      <p>Select a date for the column {{ filter.state.config.meta.fieldDisplay }} below</p>
      <div app-labeled-property label="Service Points">
        <div
          app-service-point-selector
          (valueChange)="select($event)"
          (partialValueChange)="selectPartial($event)"
          [selectedServicePoints]="filter.state.servicePoints"
          [selectedPartialValue]="partialFilter?.state?.value ?? null"
          [slim]="true"
          [config]="{allowMultiple: filter.state.config.allowMultiple ?? true}"
          [allowPartial]="!!partialFilter || false"
        ></div>
      </div>
      @if(groupFilter) {
      <div app-labeled-property label="Service Point Groups">
        <div
          app-service-point-group-selector
          (valueChange)="selectGroup($event)"
          [config]="{allowMultiple: groupFilter.state.config.allowMultiple}"
          [selectedGroups]="groupFilter.state.servicePointGroups"
          [slim]="true"
        ></div>
      </div>
      }
      <app-active-filter-list
        [filterGroup]="filter.state.config.meta.fieldGroup"
        [filterName]="filterNames()"
      ></app-active-filter-list>
    </div>
    <div app-modal-footer>
      <button class="action-btn" (click)="onClose()">Close</button>
    </div>
  }`,
})
export class ServicePointFilterModalComponent implements HeaderModal<ServicePointFilterModalInit>, OnDestroy {
  private modal = inject(NgbActiveModal)
  public filterService = inject(FilterService)

  private subscription = new Subscription()
  public filter: ServicePointFilter | undefined
  public partialFilter: ServicePointSearchFilter | undefined
  public groupFilter: ServicePointGroupFilter | undefined

  public init(
    fieldGroup: string,
    fieldName: string,
    { partialField, groupField }: ServicePointFilterModalInit = {},
  ): void {
    this.subscription.add(
      this.filterService.state(fieldGroup).subscribe((group) => {
        const filter = group[fieldName] as Filter | undefined
        if (filter?.type === 'SERVICE_POINT') {
          this.filter = filter
        }

        const partialFilter = partialField ? group[partialField] as Filter | undefined : undefined
        if (partialFilter?.type === 'SERVICE_POINT_SEARCH') {
          this.partialFilter = partialFilter
        }

        const groupFilter = groupField ? group[groupField] as Filter | undefined : undefined
        if (groupFilter?.type === 'SERVICE_POINT_GROUP') {
          this.groupFilter = groupFilter
        }
      }),
    )
  }

  protected filterNames(): string[] {
    return [
      this.filter?.state.config.meta.fieldName,
      this.partialFilter?.state.config.meta.fieldName,
      this.groupFilter?.state.config.meta.fieldName,
    ].filter((s): s is string => Boolean(s))
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  select(sp: ServicePointIdReference) {
    if(this.filter) {
      this.filterService.dispatch(
        ServicePointFilter.AddServicePointsAction(
          { servicePoints: [sp] },
          this.filter.state.config.meta,
        ),
      )
    }
  }

  selectPartial(value: string | null) {
    if(this.partialFilter) {
      this.filterService.dispatch(
        ServicePointSearchFilter.SetServicePointSearchFilterValueAction(
          { value },
          this.partialFilter.state.config.meta,
        ),
      )
    }
  }

  selectGroup(spg: ServicePointGroupWithRefs) {
    if(this.groupFilter) {
      this.filterService.dispatch(
        ServicePointGroupFilter.AddServicePointGroupsAction(
          { servicePointGroups: [spg] },
          this.groupFilter.state.config.meta,
        ),
      )
    }
  }

  onClose() {
    this.modal.close()
  }
}
